import i18next from 'i18next'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { ButtonGhost } from '../../../../utils/buttons'
import { ComponentTeaserLayout } from '../../../../utils/componentTeasers'
import { blockInviewHandler } from '../../../../utils/inview'
import { convertReactNodeToString, slugify } from '../../../../utils/utils'
import { homeInViewThreshold } from '../../home/loggedOutHome/loggedOutHomeWidget'
import { RootLayout } from '../../rootLayout'
import { ComponentTeaserData } from './componentTeasersState'

export type PageType =
  | 'home-logged-out'
  | 'home-logged-in'
  | 'component-overview'
  | 'collection-detail'
  | 'component-detail'
  | 'search-results'
  | 'dish-detail'
  | 'guest-chef-highlight'

type ComponentTeasersBaseProps = {
  kind: PageType
  title?: string
  intro?: string
  readMoreLink?: string
}

type ComponentTeasersLayoutProps = {
  componentTeasers: ComponentTeaserData[] | 'loading' | 'error'
  teaserKind: 'link-only' | 'link-save' | 'link-delete'
  isSaved: (id: number) => boolean | 'unknown'
  onSaveOrDelete: (id: number) => void
  isComparableComponents: boolean
} & ComponentTeasersBaseProps

export const ComponentTeasersInnerLayout = (p: ComponentTeasersLayoutProps) => {
  const isPublicHome = p.kind === 'home-logged-out'
  const isCollectionDetail = p.kind === 'collection-detail'
  const isDishDetail = p.kind === 'dish-detail'
  const isSearchResult = p.kind === 'search-results'
  return (
    <div className="block__inner">
      {(p.title || p.intro) && (
        <header
          className={`block__header${
            isCollectionDetail
              ? ' sr-text'
              : isSearchResult
              ? ' block__header--search'
              : ''
          }`}
        >
          {p.title && (
            <h2
              className={`block__title title--${
                isPublicHome ? 'm' : isDishDetail ? 's' : 'xs'
              }`}
              dangerouslySetInnerHTML={{ __html: p.title }}
            />
          )}
          {p.intro && (
            <p
              className={`block__intro${
                !isPublicHome ? ' block__intro--840' : ''
              }`}
            >
              {p.intro}
            </p>
          )}
        </header>
      )}
      <div className="block__content">
        <ComponentTeasersInnerContentLayout {...p} />
        {/* {p.componentTeasers === 'loading' ? (
          <RootLayout.Loader hasWrapper={true} showText={true} />
        ) : p.componentTeasers == 'error' ? (
          <RootLayout.LoadingFailed
            text={i18next.t('common:loadingFailed', {
              entity: i18next.t('component:componentsTitle').toLowerCase(),
            })}
          />
        ) : p.componentTeasers.length > 0 ? (
          <ul className={`block__list list--${p.componentTeasers.length}`}>
            {p.componentTeasers.map((teaser) => (
              <ComponentTeaserLayout
                key={teaser.Id}
                teaser={teaser}
                kind={p.teaserKind}
                isSaved={p.isSaved(teaser.Id)}
                onSaveOrDelete={() => p.onSaveOrDelete(teaser.Id)}
                isComparableComponents={p.isComparableComponents}
              />
            ))}
          </ul>
        ) : (
          <RootLayout.EmptyState
            text={
              isCollectionDetail
                ? i18next.t('collection:collectionDetailEmpty')
                : i18next.t('common:emptyState', {
                    entity: i18next
                      .t('component:componentsTitle_plural')
                      .toLowerCase(),
                  })
            }
          />
        )} */}
      </div>

      {p.readMoreLink && (
        <ButtonGhost
          kind="link"
          to={p.readMoreLink}
          size="s"
          label={i18next.t('common:more')}
          iconId="chevron-right"
          iconSize="10"
          extraClasses="block__button"
        />
      )}
    </div>
  )
}

export const ComponentTeasersWrapperLayout = (
  p: ComponentTeasersBaseProps & { children: React.ReactNode }
) => (
  <InView
    key={`io-component-teasers-${slugify(convertReactNodeToString(p.title))}`}
    as="section"
    threshold={p.kind == 'home-logged-out' ? homeInViewThreshold : 0}
    triggerOnce={true}
    onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
    className={`block block--component-teasers component-teasers--${p.kind} ${
      p.kind === 'search-results'
        ? 'component-teasers--collection-detail'
        : 'container--m gutter--m'
    }${p.readMoreLink ? ' has--link' : ''}${p.intro ? ' has--intro' : ''} cf`}
  >
    {p.children}
  </InView>
)

export const ComponentTeasersInnerContentLayout = (
  p: ComponentTeasersLayoutProps
) => {
  const isCollectionDetail = p.kind === 'collection-detail'
  return (
    <>
      {p.componentTeasers === 'loading' ? (
        <RootLayout.Loader hasWrapper={true} showText={true} />
      ) : p.componentTeasers == 'error' ? (
        <RootLayout.LoadingFailed
          text={i18next.t('common:loadingFailed', {
            entity: i18next.t('component:componentsTitle').toLowerCase(),
          })}
        />
      ) : p.componentTeasers.length > 0 ? (
        <ul className={`block__list list--${p.componentTeasers.length}`}>
          {p.componentTeasers.map((teaser) => (
            <ComponentTeaserLayout
              key={teaser.Id}
              teaser={teaser}
              kind={p.teaserKind}
              isSaved={p.isSaved(teaser.Id)}
              onSaveOrDelete={() => p.onSaveOrDelete(teaser.Id)}
              isComparableComponents={p.isComparableComponents}
            />
          ))}
        </ul>
      ) : (
        <RootLayout.EmptyState
          text={
            isCollectionDetail
              ? i18next.t('collection:collectionDetailEmpty')
              : i18next.t('common:emptyState', {
                  entity: i18next
                    .t('component:componentsTitle_plural')
                    .toLowerCase(),
                })
          }
        />
      )}
    </>
  )
}

export const ComponentTeasersLayout = (
  p: ComponentTeasersLayoutProps & { hasWrapper: boolean }
) => {
  return p.hasWrapper ? (
    <ComponentTeasersWrapperLayout
      children={<ComponentTeasersInnerLayout {...p} />}
      {...p}
    />
  ) : (
    <ComponentTeasersInnerLayout {...p} />
  )
}
